import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AnalyzerDriverConfig, AnalyzerView } from '@wodi/shared';
import { Button, Notice, Select, TextField } from '@wodi/ui';
import { useState } from 'react';

import { analyzerKeys, updateAnalyzer } from '../api/lab-analyzers.ts';
import { useOnline } from '../hooks/use-online.ts';
import { errorText } from '../stock/common.tsx';
import { toasts } from '../toasts.ts';
import styles from './lab.module.css';

// Where an analyzer's messages carry the sample number and the test code (lab connector
// plan §3.4). Vendors differ; these are read from the analyzer's interface manual.
// TODO(verify-spec): check each field against the manual of the analyzer being connected.

const ASTM_FIELDS = [
  { id: 'O.3', label: 'Order record, field 3 (Specimen ID)' },
  { id: 'O.4', label: 'Order record, field 4 (Instrument Specimen ID)' },
] as const;

const HL7_FIELDS = [
  { id: 'OBR-3', label: 'OBR-3 (filler order number)' },
  { id: 'OBR-2', label: 'OBR-2 (placer order number)' },
  { id: 'SPM-2', label: 'SPM-2 (specimen ID)' },
] as const;

const OFFSET = /^[+-](0\d|1[0-4]):[0-5]\d$/;

function componentNumber(text: string): number | null {
  const n = Number(text);
  return Number.isInteger(n) && n >= 1 && n <= 10 ? n : null;
}

export function AnalyzerDriverForm({
  analyzer,
  editable,
}: {
  analyzer: AnalyzerView;
  editable: boolean;
}) {
  const queryClient = useQueryClient();
  const online = useOnline();
  const driver = analyzer.driver;
  const [field, setField] = useState<string>(driver.specimenIdField);
  const [specimenComponent, setSpecimenComponent] = useState(String(driver.specimenIdComponent));
  const [codeComponent, setCodeComponent] = useState(String(driver.testCodeComponent));
  const [offset, setOffset] = useState(driver.utcOffset);
  const [tried, setTried] = useState(false);
  const specimen = componentNumber(specimenComponent);
  const code = componentNumber(codeComponent);
  const offsetOk = OFFSET.test(offset);
  const save = useMutation({
    mutationFn: (next: AnalyzerDriverConfig) =>
      updateAnalyzer(analyzer.id, { expectedVersion: analyzer.version, driver: next }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: analyzerKeys.all });
      toasts.show({ title: 'Analyzer settings saved' });
    },
  });
  const submit = () => {
    setTried(true);
    if (specimen === null || code === null || !offsetOk) return;
    const common = {
      specimenIdComponent: specimen,
      testCodeComponent: code,
      utcOffset: offset,
    };
    save.mutate(
      driver.protocol === 'astm_e1394'
        ? { protocol: 'astm_e1394', specimenIdField: field as 'O.3' | 'O.4', ...common }
        : { protocol: 'hl7_v2', specimenIdField: field as 'OBR-2' | 'OBR-3' | 'SPM-2', ...common },
    );
  };
  const componentError = 'A number from 1 to 10.';
  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        submit();
      }}
    >
      <Select
        label="Sample number field"
        options={driver.protocol === 'astm_e1394' ? ASTM_FIELDS : HL7_FIELDS}
        value={field}
        isDisabled={!editable}
        onChange={(value) => {
          setField(String(value));
        }}
      />
      <div className={styles.row}>
        <TextField
          label="Sample number component"
          width="sm"
          inputMode="numeric"
          value={specimenComponent}
          isReadOnly={!editable}
          onChange={setSpecimenComponent}
          {...(tried && specimen === null ? { errorMessage: componentError } : {})}
        />
        <TextField
          label={
            driver.protocol === 'astm_e1394'
              ? 'Test code component of the Universal Test ID'
              : 'Test code component of OBX-3'
          }
          width="sm"
          inputMode="numeric"
          value={codeComponent}
          isReadOnly={!editable}
          onChange={setCodeComponent}
          {...(tried && code === null ? { errorMessage: componentError } : {})}
        />
        <TextField
          label="Analyzer clock offset from UTC"
          description="East Africa Time is +03:00."
          width="sm"
          value={offset}
          isReadOnly={!editable}
          onChange={setOffset}
          {...(tried && !offsetOk ? { errorMessage: 'Write it as +03:00.' } : {})}
        />
      </div>
      {save.isError ? (
        <Notice tone="error">
          {errorText(save.error, "The settings weren't saved. Try again.")}
        </Notice>
      ) : null}
      {editable ? (
        <div>
          <Button
            type="submit"
            variant="primary"
            isDisabled={!online}
            isPending={save.isPending}
            pendingLabel="Saving"
          >
            Save settings
          </Button>
        </div>
      ) : null}
    </form>
  );
}
